// Bumble Confidential. For Internal Use Only.
import { setCookieConsentSettings } from 'lib/has-cookie-consent';

import { initialize, pageView } from 'components/google-analytics/google-analytics';

type VendorConsents = {
  consentedVendors: Array<{ _id: string }>;
};

type ConsentModeState = {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
};

// Bridges Sourcepoint's consent decision to Google Consent Mode, which every
// GA4/Google Ads tag in the GTM container reads before it fires. `gtag` is
// defined in pages/_document.tsx, ahead of the GTM snippet, and starts every
// visitor denied - this is what turns individual categories on, or re-confirms
// denial (e.g. Reject All), each time Sourcepoint reports a consent event.
function updateConsentMode(consent: ConsentModeState) {
  gtag?.('consent', 'update', consent);
}

const DENY_ALL_CONSENT_MODE: ConsentModeState = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

const SCRIPT_LOAD_DELAY = 100;
const SCRIPT_FB_ID = 'fb';
const SCRIPT_G_ADS_ID = 'g_ads';
const SCRIPT_LI_ID = 'li';
const SCRIPT_PIN_ID = 'pin';
const SCRIPT_APF_ID = 'apf';
const SCRIPT_TB_ID = 'tb';
const FB_PIXEL_ID = '5fac56cd1ba05165880458ad';
const LINKEDIN_ID = '5f1b2fbeb8e05c306f2a1ed6';
const PINTEREST_ID = '5e839a38b8e05c4e491e738e';
const APPSFLYER_ID = '5f1b2fbeb8e05c30686fd745';
const TABOOLA_ID = '5fe1db28e1860427c95c6249';
const GOOGLE_ADS_ID = '61fb9e006b4922079c088d5a';

const trackingSystem = {
  fb: '/bumble-brand-assets/libraries/tracking/facebook-pixel.js',
  g_ads: '/bumble-brand-assets/libraries/tracking/google-ads.js',
  li: '/bumble-brand-assets/libraries/tracking/linkedin.js',
  pin: '/bumble-brand-assets/libraries/tracking/pinterest.js',
  tb: '/bumble-brand-assets/libraries/tracking/taboola.js',
};

const hasConsent = (vendorConsents: VendorConsents, ID: string) =>
  vendorConsents.consentedVendors.some((vendor: { _id: string }) => vendor._id === ID);

type PixelConsentState = {
  fb: boolean;
  li: boolean;
  pin: boolean;
  apf: boolean;
  tb: boolean;
};

// None of these five vendors' scripts check consent at runtime the way GA4/Google
// Ads do via Consent Mode - once injected they run until the page unloads, so a
// later revocation (e.g. Accept All, then Reject All via the privacy manager)
// can only be enforced by reloading. `appliedPixelConsent` is the last state we
// actually applied this session; null means "nothing applied yet" (the initial
// page-load consent event), which should never trigger a reload on its own.
let appliedPixelConsent: PixelConsentState | null = null;

function reloadIfPixelConsentRevoked(nextPixelConsent: PixelConsentState): boolean {
  const previousPixelConsent = appliedPixelConsent;
  appliedPixelConsent = nextPixelConsent;

  if (!previousPixelConsent) {
    return false;
  }

  const consentWasRevoked = (Object.keys(nextPixelConsent) as Array<keyof PixelConsentState>).some(
    (vendor) => previousPixelConsent[vendor] && !nextPixelConsent[vendor],
  );

  if (consentWasRevoked) {
    window.location.reload();

    return true;
  }

  return false;
}

function setTrackingPlatformById(id: string) {
  const scriptId = `script-${id}`;

  // we call script on each page load, if script loaded - it won't load again
  if (document.getElementById(scriptId)) {
    return;
  }

  if (trackingSystem) {
    let script = null;
    script = document.createElement('script');
    script.id = scriptId;
    // @ts-ignore
    script.src = trackingSystem[id];
    document.body.appendChild(script);
  }
}

type UspData = { uspString?: string };

// USP v1 string format: "<version><NoticeGiven><OptedOut><LspaCovered>", e.g. "1YNN".
// Position 3 (index 2) is the opt-out-of-sale/share flag: 'Y' = opted out, 'N' = not
// opted out, '-' = not yet determined. '-' is treated as not-opted-out, matching
// CCPA's opt-out-by-default regime - the visitor hasn't acted, so nothing changes yet.
function hasCcpaOptedOut(uspString: string | undefined): boolean {
  return typeof uspString === 'string' && uspString.length >= 3 && uspString[2] === 'Y';
}

// The IAB US Privacy API (unlike TCF) has no addEventListener/push mechanism - only
// this pull-based getUSPData call - so this has to be invoked explicitly whenever we
// need the current opt-out state, rather than registered once like __tcfapi.
//
// `onFailure` controls what happens if Sourcepoint can't confirm the status: on the
// initial page load (no prior signal at all) "not opted out" is the correct
// opt-out-by-default read, but right after the visitor submits a choice in the
// privacy manager, failing open would silently override the action they just took -
// callers there should pass 'deny' instead.
function readUspOptOutStatus(
  onResult: (hasOptedOut: boolean) => void,
  onFailure: 'grant' | 'deny' = 'grant',
) {
  const fallback = onFailure === 'deny';
  try {
    __uspapi('getUSPData', 1, (data: UspData, success: boolean) => {
      onResult(success ? hasCcpaOptedOut(data?.uspString) : fallback);
    });
  } catch {
    onResult(fallback);
  }
}

// Decision (JS-30699): analytics_storage is denied on opt-out too, not just the ad
// signals - product/marketing don't rely on this data for Web, so there's no known
// cost to denying it. Revisit if that turns out to have a negative consequence.
function applyCcpaConsentState(hasOptedOut: boolean) {
  updateConsentMode({
    ad_storage: hasOptedOut ? 'denied' : 'granted',
    ad_user_data: hasOptedOut ? 'denied' : 'granted',
    ad_personalization: hasOptedOut ? 'denied' : 'granted',
    analytics_storage: hasOptedOut ? 'denied' : 'granted',
  });
  setCookieConsentSettings({ functional: !hasOptedOut, analytics: !hasOptedOut });

  // Facebook/LinkedIn/Pinterest/AppsFlyer/Taboola/Google Ads can't be turned off in
  // place once loaded (see JS-30695) - if the visitor just opted out mid-session
  // (via the CCPA privacy manager, after being granted by default on page load),
  // reload now instead of skipping scripts on a page that's about to be replaced.
  if (
    reloadIfPixelConsentRevoked({
      fb: !hasOptedOut,
      li: !hasOptedOut,
      pin: !hasOptedOut,
      apf: !hasOptedOut,
      tb: !hasOptedOut,
    })
  ) {
    return;
  }

  if (!hasOptedOut) {
    initialize();
    pageView();

    setTrackingPlatformById(SCRIPT_G_ADS_ID);
    setTrackingPlatformById(SCRIPT_APF_ID);
    setTrackingPlatformById(SCRIPT_FB_ID);
    setTrackingPlatformById(SCRIPT_LI_ID);
    setTrackingPlatformById(SCRIPT_PIN_ID);
    setTrackingPlatformById(SCRIPT_TB_ID);
  }
}

const PRIVACY_MANAGER_ACTION_TIMEOUT = 2000;

// Bridges _sp_.config.events.onPrivacyManagerAction (pages/_document.tsx) - fires for
// both GDPR and CCPA privacy manager submissions. GDPR ones are already handled by the
// __tcfapi addEventListener in containers/page/page-container.tsx, so this only acts
// when GDPR doesn't apply - re-checking the US Privacy opt-out status and applying it.
//
// The visitor just submitted a choice, so this can't silently do nothing: a timeout
// fallback checks CCPA anyway if getTCData never calls back, and a failed
// getUSPData read fails toward denying rather than granting - unlike the initial
// page-load check, overriding the action just taken would be worse than over-denying.
export function handlePrivacyManagerAction() {
  let handled = false;

  const applyCcpaCheck = () => {
    if (handled) {
      return;
    }

    handled = true;
    readUspOptOutStatus(applyCcpaConsentState, 'deny');
  };

  setTimeout(applyCcpaCheck, PRIVACY_MANAGER_ACTION_TIMEOUT);

  __tcfapi?.('getTCData', 2, (tcData: { gdprApplies?: boolean } | undefined, success: boolean) => {
    if (handled) {
      return;
    }

    if (success && tcData?.gdprApplies) {
      handled = true;

      return;
    }

    applyCcpaCheck();
  });
}

const getVendorsData = function () {
  __tcfapi(
    'getCustomVendorConsents',
    2,
    (vendorConsents: VendorConsents, successForConsent: boolean) => {
      if (successForConsent) {
        const hasGoogleAdsConsent = hasConsent(vendorConsents, GOOGLE_ADS_ID);
        const hasFbPixelConsent = hasConsent(vendorConsents, FB_PIXEL_ID);
        const hasLinkedinConsent = hasConsent(vendorConsents, LINKEDIN_ID);
        const hasPinterestConsent = hasConsent(vendorConsents, PINTEREST_ID);
        const hasAppsFlyerConsent = hasConsent(vendorConsents, APPSFLYER_ID);
        const hasTaboolaConsent = hasConsent(vendorConsents, TABOOLA_ID);

        // Fires on every Sourcepoint consent event (initial load, Reject All,
        // Accept All, or a partial save from the privacy manager) - not just once -
        // so a rejection made after tags have already loaded still turns them off.
        //
        // TODO(JS-30665): analytics_storage is piggybacking on the Google Ads vendor
        // consent (GOOGLE_ADS_ID) because there's no separate Google Analytics/GA4
        // vendor ID configured anywhere in this codebase today. If Sourcepoint's
        // vendor list distinguishes an analytics category from ads, this will grant
        // or deny GA4 based on the wrong consent choice. Confirm the real vendor ID
        // with whoever owns the Sourcepoint property config and split this out.
        updateConsentMode({
          ad_storage: hasGoogleAdsConsent ? 'granted' : 'denied',
          ad_user_data: hasGoogleAdsConsent ? 'granted' : 'denied',
          ad_personalization: hasGoogleAdsConsent ? 'granted' : 'denied',
          analytics_storage: hasGoogleAdsConsent ? 'granted' : 'denied',
        });

        setCookieConsentSettings({
          functional:
            hasFbPixelConsent ||
            hasLinkedinConsent ||
            hasPinterestConsent ||
            hasAppsFlyerConsent ||
            hasTaboolaConsent,
          analytics: hasGoogleAdsConsent,
        });

        // Facebook/LinkedIn/Pinterest/AppsFlyer/Taboola can't be turned off in
        // place once loaded (see JS-30695) - if any of them just got revoked,
        // reload now instead of injecting/skipping scripts on a page that's
        // about to be replaced anyway.
        if (
          reloadIfPixelConsentRevoked({
            fb: hasFbPixelConsent,
            li: hasLinkedinConsent,
            pin: hasPinterestConsent,
            apf: hasAppsFlyerConsent,
            tb: hasTaboolaConsent,
          })
        ) {
          return;
        }

        if (hasAppsFlyerConsent) {
          setTimeout(function () {
            setTrackingPlatformById(SCRIPT_APF_ID);
          }, SCRIPT_LOAD_DELAY);
        }

        if (hasFbPixelConsent) {
          setTimeout(function () {
            setTrackingPlatformById(SCRIPT_FB_ID);
          }, SCRIPT_LOAD_DELAY);
        }

        if (hasGoogleAdsConsent) {
          setTimeout(() => {
            setTrackingPlatformById(SCRIPT_G_ADS_ID);

            initialize();
            pageView();
          }, 100);
        }

        if (hasLinkedinConsent) {
          setTimeout(function () {
            setTrackingPlatformById(SCRIPT_LI_ID);
          }, SCRIPT_LOAD_DELAY);
        }

        if (hasPinterestConsent) {
          setTimeout(function () {
            setTrackingPlatformById(SCRIPT_PIN_ID);
          }, SCRIPT_LOAD_DELAY);
        }

        if (hasTaboolaConsent) {
          setTimeout(function () {
            setTrackingPlatformById(SCRIPT_TB_ID);
          }, SCRIPT_LOAD_DELAY);
        }
      } else {
        // Sourcepoint couldn't confirm vendor consent - stay denied rather than
        // leaving tags (and the cookie_settings record) in whatever state they
        // were last set to.
        updateConsentMode(DENY_ALL_CONSENT_MODE);
        setCookieConsentSettings({ functional: false, analytics: false });
        reloadIfPixelConsentRevoked({ fb: false, li: false, pin: false, apf: false, tb: false });
      }
    },
  );
};

export function handleCookieConsent(
  tcData: { gdprApplies: boolean; eventStatus: string; listenerId: string | number },
  success: boolean,
) {
  if (success) {
    if (
      !tcData.gdprApplies &&
      (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'cmpuishown')
    ) {
      // Non-GDPR countries (US/CCPA, rest of world): opt-out regime, consent is
      // assumed by default - but a returning visitor may have already opted out via
      // the CCPA privacy manager in an earlier session, so check before granting.
      setTimeout(() => {
        readUspOptOutStatus(applyCcpaConsentState);
      }, SCRIPT_LOAD_DELAY);
    } else if (tcData.gdprApplies) {
      const cookieContainer: HTMLElement | null = document.querySelector('.cookie-notification');

      if (cookieContainer) {
        cookieContainer.style.display = 'none';
      }
      // we call script on each page load, if script loaded - it won't load again
      getVendorsData();
    }
  }

  return tcData.listenerId;
}

// Bumble Confidential. For Internal Use Only.
// Starts listening for TCF consent events; returns a cleanup function.
export function listenForCookieConsentEvents(): () => void {
  let listenerId: string | number | undefined;
  let disposed = false;
  let removed = false;

  const removeListener = () => {
    // Compare against undefined, not falsiness: the first listener's ID is 0.
    if (!removed && listenerId !== undefined) {
      removed = true;
      // The CMP throws "invalid callback function" if this callback is null.
      __tcfapi?.('removeEventListener', 2, () => undefined, listenerId);
    }
  };

  // Sourcepoint's addEventListener returns undefined - the listener ID only
  // arrives in the event data, so it has to be captured from the first event.
  __tcfapi?.(
    'addEventListener',
    2,
    (tcData: Parameters<typeof handleCookieConsent>[0] | undefined, success: boolean) => {
      if (listenerId === undefined && success && tcData?.listenerId !== undefined) {
        listenerId = tcData.listenerId;
      }

      if (disposed) {
        // Cleanup ran before the first event delivered the ID: remove it now.
        removeListener();

        return;
      }

      if (tcData) {
        handleCookieConsent(tcData, success);
      }
    },
  );

  return () => {
    disposed = true;
    removeListener();
  };
}

function displayGDPR(event: Event) {
  event.preventDefault();

  try {
    //@ts-ignore
    window._sp_.gdpr.loadPrivacyManagerModal();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    window.location.href = '/cookie-policy';
  }
}

function displayCCPA(event: Event) {
  event.preventDefault();

  try {
    //@ts-ignore
    window._sp_.ccpa.loadPrivacyManagerModal('708248');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    window.location.href = '/cookie-policy';
  }
}

export const onUsePrivacyManagerHandler = () => {
  const privacyManagerGDPR = document.querySelector('.js-manage-cookies');
  const privacyManagerCCPA = document.querySelector('.js-ccpa-privacy');

  if (privacyManagerGDPR) {
    privacyManagerGDPR.addEventListener('click', displayGDPR);
  }

  if (privacyManagerCCPA) {
    privacyManagerCCPA.addEventListener('click', displayCCPA);
  }

  return () => {
    if (privacyManagerGDPR) {
      privacyManagerGDPR.removeEventListener('click', displayGDPR);
    }

    if (privacyManagerCCPA) {
      privacyManagerCCPA.removeEventListener('click', displayCCPA);
    }
  };
};
